/**
 * FileDetailPanel
 *
 * Slide-out sidebar showing file provenance: uploader, date, provider, bucket, key,
 * encryption type, version history, and download/delete actions.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  X, Download, Trash2, ExternalLink, Lock, Shield, Key,
  FileText, Image, Film, Music, File as FileIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { FileRecord, FileService } from '@/services/api/fileService';
import * as errorUtils from '@/utils/errors';

interface FileDetailPanelProps {
  file: FileRecord | null;
  orgSlug: string;
  onClose: () => void;
  onDelete?: (file: FileRecord) => void;
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleString();
};

export const FileDetailPanel: React.FC<FileDetailPanelProps> = ({ file, orgSlug, onClose, onDelete }) => {
  const [versions, setVersions] = useState<FileRecord[]>([]);
  const [showPassphraseDialog, setShowPassphraseDialog] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (file && file.version > 1) {
      FileService.listVersions(orgSlug, file._id).then((resp) => {
        setVersions(resp.data || []);
      }).catch(() => {});
    } else {
      setVersions([]);
    }
  }, [file?._id, orgSlug]);

  const handleDownload = useCallback(async (passInput?: string) => {
    if (!file) return;

    if (file.encryptionType === 'user_protected' && !passInput) {
      setShowPassphraseDialog(true);
      return;
    }

    setDownloading(true);
    try {
      const response = await FileService.getDownloadUrl(orgSlug, file._id, passInput);
      if (response.data?.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      }
    } catch (error: unknown) {
      alert(errorUtils.getApiErrorMessage(error, 'Download failed'));
    }
    setDownloading(false);
    setShowPassphraseDialog(false);
    setPassphrase('');
  }, [file, orgSlug]);

  if (!file) return null;

  const isImage = file.contentType.startsWith('image/');

  const detailRows = [
    { label: 'Content Type', value: file.contentType },
    { label: 'Size', value: formatSize(file.size) },
    { label: 'Provider', value: file.storageProvider },
    { label: 'Bucket', value: file.bucketName },
    { label: 'Key', value: file.key },
    { label: 'Scope', value: file.scope },
    { label: 'Visibility', value: file.visibility },
    { label: 'Encryption', value: file.encryptionType },
    { label: 'Version', value: `v${file.version}${file.isLatestVersion ? ' (latest)' : ''}` },
    { label: 'Storage Class', value: file.storageClass },
    { label: 'Access Count', value: String(file.accessCount) },
    { label: 'Created', value: formatDate(file.createdAt) },
    { label: 'Updated', value: formatDate(file.updatedAt) },
    ...(file.etag ? [{ label: 'ETag', value: file.etag }] : []),
    ...(file.tags.length > 0 ? [{ label: 'Tags', value: file.tags.join(', ') }] : []),
  ];

  return (
    <>
      <div className="fixed inset-y-0 right-0 top-[73px] w-96 bg-background border-l shadow-lg z-40 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold truncate">{file.filename}</h3>
          <Button size="sm" variant="ghost" onClick={onClose} className="h-8 w-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Preview */}
        {isImage && file.thumbnails?.large && (
          <div className="p-4 border-b">
            <img src={file.thumbnails.large} alt={file.filename} className="w-full rounded" />
          </div>
        )}

        {/* Details */}
        <div className="flex-1 overflow-auto p-4 space-y-2">
          {file.description && (
            <p className="text-sm text-muted-foreground mb-3">{file.description}</p>
          )}

          {detailRows.map(({ label, value }) => (
            <div key={label} className="flex justify-between text-sm">
              <span className="text-muted-foreground">{label}</span>
              <span className="font-mono text-xs text-right max-w-[200px] truncate" title={value}>{value}</span>
            </div>
          ))}

          {/* Version History */}
          {versions.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <h4 className="text-sm font-medium mb-2">Version History</h4>
              {versions.map((v) => (
                <div key={v._id} className="flex items-center justify-between text-xs py-1">
                  <span>v{v.version}</span>
                  <span className="text-muted-foreground">{formatSize(v.size)}</span>
                  <span className="text-muted-foreground">{formatDate(v.createdAt)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 p-4 border-t">
          <Button size="sm" className="flex-1" onClick={() => handleDownload()} disabled={downloading}>
            <Download className="w-4 h-4 mr-1" />
            Download
          </Button>
          {onDelete && (
            <Button size="sm" variant="destructive" onClick={() => onDelete(file)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Passphrase Dialog */}
      <Dialog open={showPassphraseDialog} onOpenChange={setShowPassphraseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enter Passphrase</DialogTitle>
            <DialogDescription>This file is protected with a passphrase.</DialogDescription>
          </DialogHeader>
          <Input
            type="password"
            placeholder="Enter passphrase"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleDownload(passphrase)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPassphraseDialog(false)}>Cancel</Button>
            <Button onClick={() => handleDownload(passphrase)} disabled={!passphrase}>Download</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
